import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  FileText,
  Upload,
  Download,
  Trash2,
  File,
  Image,
  FileSpreadsheet,
  Presentation,
  Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DocumentUploadDialog } from "./documents/DocumentUploadDialog";
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  documentOpenTarget,
  formatFileSize,
  openDocument,
} from "./documents/documentCategories";

interface Document {
  id: string;
  name: string;
  description: string | null;
  fileName: string;
  fileUrl: string;
  fileSize: number | null;
  mimeType: string | null;
  category: "teaser" | "cim" | "mva" | "engagement_letter" | "nda" | "financials" | "legal" | "contract" | "report" | "other";
  companyId: string | null;
  contactId: string | null;
  dealId: string | null;
  uploadedBy: string;
  isConfidential: boolean;
  accessLevel: string;
  version: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

interface DocumentManagerProps {
  companyId?: string;
  contactId?: string;
  dealId?: string;
}

const getFileIcon = (mimeType: string | null) => {
  if (!mimeType) return File;
  if (mimeType.startsWith("image/")) return Image;
  if (mimeType.includes("pdf")) return FileText;
  if (mimeType.includes("sheet") || mimeType.includes("excel")) return FileSpreadsheet;
  if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return Presentation;
  return FileText;
};

export function DocumentManager({ companyId, contactId, dealId }: DocumentManagerProps) {
  const queryClient = useQueryClient();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const params = new URLSearchParams();
  if (companyId) params.append("companyId", companyId);
  if (contactId) params.append("contactId", contactId);
  if (dealId) params.append("dealId", dealId);

  const { data: docs = [], isLoading } = useQuery<Document[]>({
    queryKey: ["/api/documents", companyId, contactId, dealId],
    queryFn: async () => {
      const res = await fetch(`/api/documents?${params}`);
      if (!res.ok) throw new Error("Failed to fetch documents");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/documents/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete document");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      toast.success("Document deleted");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Documents</h3>
          <p className="text-sm text-muted-foreground">
            {docs.length} document{docs.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={() => setIsUploadDialogOpen(true)} className="gap-2">
          <Upload className="w-4 h-4" />
          Upload Document
        </Button>
      </div>

      {/* Document List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-48 bg-muted rounded" />
                    <div className="h-3 w-24 bg-muted rounded" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : docs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold mb-1">No documents yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload documents to keep all deal files in one place
            </p>
            <Button onClick={() => setIsUploadDialogOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload First Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {docs.map((doc) => {
            const FileIcon = getFileIcon(doc.mimeType);
            const missing = documentOpenTarget(doc.fileUrl) === "missing";
            return (
              <Card key={doc.id} className="group hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <FileIcon className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium truncate">{doc.name}</h4>
                        {doc.isConfidential && (
                          <Lock className="w-3.5 h-3.5 text-amber-500" />
                        )}
                        {missing && (
                          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                            re-upload needed
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span>{doc.fileName}</span>
                        <span>•</span>
                        <span>{formatFileSize(doc.fileSize)}</span>
                        <span>•</span>
                        <span>v{doc.version}</span>
                      </div>
                      {doc.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {doc.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Badge className={cn("text-xs", CATEGORY_COLORS[doc.category])}>
                          {CATEGORY_LABELS[doc.category]}
                        </Badge>
                        {doc.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={missing}
                        title={missing ? "File was never stored — re-upload it" : "Download"}
                        onClick={() => openDocument(doc.fileUrl, doc.fileName)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => deleteMutation.mutate(doc.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DocumentUploadDialog
        open={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        target={{ companyId, contactId, dealId }}
      />
    </div>
  );
}

export default DocumentManager;
